import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Container,
  TextField,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  Button,
  Alert,
  AlertTitle,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  Backdrop,
  Tabs,
  Tab,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CategoryIcon from "@mui/icons-material/Category";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FilterHdrIcon from '@mui/icons-material/FilterHdr';

import supabase from "../../../utils/supabase";
import { formatScientificNameLabel, extractAuthorNames, shouldBracketAuthorship } from "../../../utils/scientificNameFormatter";
import { searchScientificNames, normalizeSearchQuery } from "../../../utils/scientificNameSearch";
import TaxonomicTree from "./TaxonomicTree";
import DialogDisplayScientificNameDetails from "./DialogDisplayScientificNameDetails/DialogDisplayScientificNameDetails";
// 全件ロードを避けるため、必要なノードを遅延取得する
const MIN_FIELDS = "id,name_spell_valid,name_spell_original,current_rank,current_invalid_type,current_parent,original_parent,authority_year,authority_from_gbif,valid_name_id,gbif_id,merged_gbif_ids,gbif_usage_key,extant_fossil";
const AUTHOR_JOIN = "scientific_name_and_author!left(author_order,author_relation:scientific_name_and_author_researcher_id_fkey(id,first_name,last_name))";
const BASE_SELECT = `${MIN_FIELDS},${AUTHOR_JOIN}`;
const CHILD_PAGE_SIZE = 50;

// --- ダークテーマ定義 ---
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#7fffd4' },
    background: { default: '#000000', paper: '#1a1a1a' },
    text: { primary: '#e0e0e0', secondary: '#b0bec5' },
    action: { active: '#7fffd4' },
  },
  typography: {
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
    h3: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(127,255,212,0.3)',
          borderRadius: '12px',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: '1px solid rgba(127,255,212,0.5)',
          borderRadius: '8px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          borderColor: 'rgba(127,255,212,0.5)',
          color: '#7fffd4',
          '&:hover': {
            backgroundColor: 'rgba(127,255,212,0.1)',
            borderColor: '#7fffd4',
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
          border: '1px solid rgba(127,255,212,0.4)',
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(127,255,212,0.2)',
        }
      }
    }
  },
});

const isInvalidRankName = (rank) => {
  const r = (rank || "").toLowerCase();
  return (
    r.includes("synonym") ||
    r.includes("syn.") ||
    r.includes(" syn ") ||
    r === "homonym" ||
    r === "invalid"
  );
};

export default function TaxonomyExplore() {
  const { t, i18n } = useTranslation(["taxonomy", "common"]);
  const location = useLocation();
  const navigate = useNavigate();

  const [allScientificNames, setAllScientificNames] = useState([]);
  const [scientificNameAuthors, setScientificNameAuthors] = useState([]);
  const sciMap = useMemo(() => new Map(allScientificNames.map((n) => [n.id, n])), [allScientificNames]);
  
  // タブ管理State
  const [tabs, setTabs] = useState([{ id: 'tree', label: t('taxonomy:tab.tree') }]);
  const [activeTab, setActiveTab] = useState('tree');
  const [tabData, setTabData] = useState({}); // { [tabId]: { node, latestAudit } }
  const [initializing, setInitializing] = useState(true);
  const [initialLoadError, setInitialLoadError] = useState(null);
  const [selectedScientificName, setSelectedScientificName] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // サジェストとロード状況
  const [searchInput, setSearchInput] = useState("");
  const [combineMode, setCombineMode] = useState("AND");
  const [matchMode, setMatchMode] = useState("partial");
  const [fuzzyEnabled, setFuzzyEnabled] = useState(true);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [dialogSearchResults, setDialogSearchResults] = useState([]);
  const [dialogSearchLoading, setDialogSearchLoading] = useState(false);
  const [actorNameMap, setActorNameMap] = useState(new Map());

  const mergeMemberNames = useCallback((members) => {
    if (!members) return;
    setActorNameMap((prev) => {
      const map = new Map(prev);
      (members || []).forEach((m) => {
        if (m.actor_id) map.set(m.actor_id, m.display_name);
        if (m.auth_user_id) map.set(m.auth_user_id, m.display_name);
        map.set(m.id, m.display_name);
      });
      return map;
    });
  }, []);

  const fetchMembersWithFallback = useCallback(async (ids = null) => {
    const hasIds = Array.isArray(ids) && ids.length > 0;
    const quoted = hasIds ? ids.map((id) => `"${id}"`).join(",") : "";
    let q = supabase.from("admin_team_members").select("id, auth_user_id, display_name");
    if (hasIds) {
      q = q.or(`id.in.(${quoted}),auth_user_id.in.(${quoted})`);
    }
    const { data, error } = await q;
    if (error) throw error;
    return data;
  }, []);

  const resolveActorName = useCallback((log) => {
    if (log?.actor_id && actorNameMap.has(log.actor_id)) {
      const disp = actorNameMap.get(log.actor_id);
      if (disp && typeof disp === "object") {
        const lang = (i18n.language || "").split("-")[0];
        return disp[lang] || disp.en || Object.values(disp)[0];
      }
      return disp;
    }
    return t("taxonomy:details.unknownUser");
  }, [actorNameMap, i18n.language, t]);

  // 子ノードのフェッチ済み管理
  const [fetchedChildren, setFetchedChildren] = useState(new Map()); // parentId -> last loaded page (0-index)
  const [loadingChildren, setLoadingChildren] = useState(new Set());
  const [childrenTotals, setChildrenTotals] = useState({}); // parentId -> total children count
  const [expandedNodes, setExpandedNodes] = useState({});
  const [persistExpanded, setPersistExpanded] = useState(new Set());
  const [descendantCountMap, setDescendantCountMap] = useState({}); // parentId -> { valid: {rank: total}, invalid: {status: total} }
  const descendantCountPendingRef = useRef(new Set());
  const descendantCountMapRef = useRef(descendantCountMap);

  // --- タブ内ヘッダーのフォーマットロジック ---
  const isBotActor = useCallback((log) => {
    const actor = String(resolveActorName(log)).toLowerCase();
    return actor.includes("bot");
  }, [resolveActorName]);

  const botByAudit = useMemo(() => {
    if (!activeTab || activeTab === 'tree') return false;
    return isBotActor(tabData[activeTab]?.latestAudit);
  }, [activeTab, tabData, isBotActor]);

  const latestAuditText = useMemo(() => {
    if (!activeTab || activeTab === 'tree') return null;
    const latestAudit = tabData[activeTab]?.latestAudit;
    if (!latestAudit) return null;
    const when = latestAudit.changed_at || latestAudit.created_at;
    const who = resolveActorName(latestAudit);
    if (!when) return t("taxonomy:details.updatedBy", { who });
    const d = new Date(when);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return t("taxonomy:details.updatedDateBy", { date: dateStr, who });
  }, [activeTab, tabData, t]);

  const formatAuthorsInline = useCallback((names = []) => {
    if (!names.length) return "";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;
  }, []);

  const titleAuthors = useMemo(() => {
    if (!activeTab || activeTab === 'tree') return "";
    const node = tabData[activeTab]?.node;
    if (!node?.id) return "";
    const rels = scientificNameAuthors
      .filter((r) => r.scientific_name_id === node.id)
      .sort((a, b) => (a.author_order ?? 0) - (b.author_order ?? 0));
    const names = extractAuthorNames({
      _authority_authors: rels.map((r) => r.researcher?.last_name_eng || r.researcher?.last_name || r.researcher?.first_name || ""),
      authors_arr: rels.map((r) => r.researcher?.last_name_eng || r.researcher?.last_name || r.researcher?.first_name || ""),
    });
    return formatAuthorsInline(names);
  }, [activeTab, tabData, scientificNameAuthors, formatAuthorsInline]);

  const formatSingleNodeName = useCallback((sn) => {
    if (!sn) return "";

    const rels = scientificNameAuthors
      .filter((r) => r.scientific_name_id === sn.id)
      .sort((a, b) => (a.author_order ?? 0) - (b.author_order ?? 0));
    const namesFromRels = rels.map((r) => r.researcher?.last_name_eng || r.researcher?.last_name || r.researcher?.first_name || "").filter(Boolean);
    const names = namesFromRels.length ? namesFromRels : extractAuthorNames(sn);
    const authorsText = formatAuthorsInline(names);
    const year = sn.authority_year || "";
    const gbifAuth = sn.authority_from_gbif || "";
    let authority;
    if (authorsText) {
      authority = authorsText && year ? `${authorsText}, ${year}` : authorsText || year || "";
    } else if (gbifAuth) {
      authority = gbifAuth;
    } else {
      authority = year;
    }

    const ancestors = [];
    let cur = sn;
    const seen = new Set();
    while (cur?.current_parent && !seen.has(cur.current_parent)) {
      seen.add(cur.current_parent);
      const parent = sciMap.get(cur.current_parent);
      if (!parent) break;
      ancestors.unshift(parent);
      cur = parent;
    }

    const italics = (n) => (n ? `<i>${n}</i>` : "");
    const rankNorm = (sn.current_rank || "").trim().toLowerCase();
    const validName = sn.name_spell_valid || "";
    const originalRaw = (sn.name_spell_original || "").split("|")[0].trim();
    const originalNoAuth = originalRaw.replace(/\([^)]*\)/g, " ").trim();
    const tokens = originalNoAuth.split(/\s+/).filter(Boolean);
    const tokenGenus = tokens[0] || "";
    const tokenSpecies = tokens[1] || "";
    const tokenSubspecies = tokens[2] || "";
    let nameStr = "";

    if (["genus", "species", "subspecies"].includes(rankNorm)) {
      const epithet = italics(validName);
      if (rankNorm === "species" || rankNorm === "subspecies") {
        const genus = ancestors.find((a) => (a.current_rank || "").toLowerCase() === "genus");
        const subgenus = ancestors.find((a) => (a.current_rank || "").toLowerCase() === "subgenus");
        const genusName = italics(genus?.name_spell_valid || tokenGenus || sn._parent_label || t("taxonomy:labels.unknownGenus"));
        const subgenusPart = subgenus ? `(${italics(subgenus.name_spell_valid)}) ` : "";

        if (rankNorm === "species") {
          const speciesEpithet = epithet || italics(tokenSpecies);
          nameStr = `${genusName} ${subgenusPart}${speciesEpithet}`.trim();
        } else {
          const speciesAncestor = ancestors.find((a) => (a.current_rank || "").toLowerCase() === "species");
          const speciesEpithet = italics(speciesAncestor?.name_spell_valid || tokenSpecies || "");
          const subspEpithet = epithet || italics(tokenSubspecies);
          nameStr = `${genusName} ${subgenusPart}${speciesEpithet} ${subspEpithet}`.trim();
        }
      } else {
        nameStr = epithet;
      }
    } else if (rankNorm === "subgenus") {
      const genus = ancestors.find((a) => (a.current_rank || "").toLowerCase() === "genus");
      nameStr = `${italics(genus?.name_spell_valid || tokenGenus || t("taxonomy:labels.unknownGenus"))} (${italics(validName)})`;
    } else {
      nameStr = validName;
    }

    if (authority && shouldBracketAuthorship(sn, sciMap)) {
      authority = `(${authority})`;
    }

    return `${nameStr}${authority ? ` ${authority}` : ""}`;
  }, [scientificNameAuthors, sciMap, formatAuthorsInline, t]);

  useEffect(() => {
    descendantCountMapRef.current = descendantCountMap;
  }, [descendantCountMap]);

  const normalizeDescendantCounts = useCallback((rows = []) => {
    const result = { valid: {}, invalid: {} };
    const isInvalidKey = (key) => {
      if (!key) return false;
      const k = key.toLowerCase();
      return (
        k === "synonym" ||
        k === "homonym" ||
        k === "invalid" ||
        k.includes("synonym") ||
        k.includes("syn.") ||
        k.includes("syn ")
      );
    };

    (rows || []).forEach((r) => {
      const key = (r.rank || r.status || r.key || "").toLowerCase();
      const total = r.total ?? r.count ?? 0;
      const kind = (r.kind || r.type || "").toLowerCase();
      if (!key) return;
      const target =
        kind === "invalid" || isInvalidKey(key)
          ? "invalid"
          : "valid";
      result[target][key] = total;
    });
    if (!Object.keys(result.valid).length && !Object.keys(result.invalid).length && rows.length) {
      rows.forEach((r) => {
        const key = (r.rank || "").toLowerCase();
        const total = r.total ?? 0;
        if (key) result.valid[key] = total;
      });
    }
    return result;
  }, []);

  const resultsContainerRef = useRef(null);
  const mergeNodes = useCallback((nodes) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return;
    setAllScientificNames((prev) => {
      const map = new Map(prev.map((n) => [n.id, n]));
      nodes.forEach((n) => {
        if (n?.id) map.set(n.id, { ...map.get(n.id), ...n });
      });
      return Array.from(map.values());
    });
  }, []);

  const mergeAuthors = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    setScientificNameAuthors((prev) => {
      const map = new Map(prev.map((r) => [r.id, r]));
      rows.forEach((r) => {
        if (r?.id) map.set(r.id, r);
      });
      return Array.from(map.values());
    });
  }, []);

  const mergeAuthorsFromRows = useCallback((rows = []) => {
    const rels = [];
    rows.forEach((row) => {
      if (!row?.id || !Array.isArray(row.scientific_name_and_author)) return;
      row.scientific_name_and_author.forEach((rel) => {
        const { author_relation, ...rest } = rel;
        rels.push({
          ...rest,
          researcher: author_relation,
          id: `${row.id}-${rel.author_order ?? 0}-${author_relation?.id ?? ""}`,
          scientific_name_id: row.id,
        });
      });
    });
    if (rels.length) mergeAuthors(rels);
  }, [mergeAuthors]);

  const fetchDescendantCounts = useCallback(async (id) => {
    if (!id) return;
    if (descendantCountMapRef.current[id]) return;
    if (descendantCountPendingRef.current.has(id)) return;
    descendantCountPendingRef.current.add(id);
    try {
      const { data, error } = await supabase.rpc("taxonomy_descendant_counts", { p_id: id });
      if (error) throw error;
      const normalized = normalizeDescendantCounts(data || []);
      setDescendantCountMap((prev) => ({ ...prev, [id]: normalized }));
    } catch (e) {
      console.warn("[Taxonomy] fetchDescendantCounts error", e.message || e);
    } finally {
      descendantCountPendingRef.current.delete(id);
    }
  }, [normalizeDescendantCounts]);

  const ensureDescendantCounts = useCallback(
    async (ids = []) => {
      const uniq = Array.from(new Set(ids.filter(Boolean)));
      if (!uniq.length) return;
      await Promise.all(uniq.map((cid) => fetchDescendantCounts(cid)));
    },
    [fetchDescendantCounts]
  );

  const fetchAuthorsForIds = useCallback(async (ids) => {
    const uniq = Array.from(new Set((ids || []).filter(Boolean)));
    if (!uniq.length) return;
    const { data, error } = await supabase
      .from("scientific_name_and_author")
      .select("scientific_name_id, author_order, author_relation:scientific_name_and_author_researcher_id_fkey(id, first_name, last_name)")
      .in("scientific_name_id", uniq);
    if (error) {
      console.warn("[Taxonomy] fetchAuthors error:", error.message);
      return;
    }
    const dataWithIds = (data || []).map(rel => {
      const { author_relation, ...rest } = rel;
      return {
        ...rest,
        researcher: author_relation,
        id: `${rel.scientific_name_id}-${rel.author_order ?? 0}-${author_relation?.id ?? ""}`
      };
    });
    mergeAuthors(dataWithIds);
  }, [mergeAuthors]);

  const fetchNodeById = useCallback(async (id) => {
    if (!id) return null;
    const { data, error } = await supabase
      .from("scientific_names")
      .select(BASE_SELECT)
      .eq("id", id)
      .maybeSingle();
    if (error) {
      console.warn("[Taxonomy] fetchNodeById error:", error.message);
      return null;
    }
    if (data) {
      mergeNodes([data]);
      mergeAuthorsFromRows([data]);
    }
    return data;
  }, [mergeNodes, mergeAuthorsFromRows]);

  const fetchChildren = useCallback(
    async (parentId, page = 0) => {
      if (!parentId) return;
      const lastLoaded = fetchedChildren.get(parentId);
      if (typeof lastLoaded === "number" && page <= lastLoaded) return;
      setLoadingChildren((prev) => {
        if (prev.has(parentId)) return prev;
        const next = new Set(prev);
        next.add(parentId);
        return next;
      });
      try {
        const from = page * CHILD_PAGE_SIZE;
        const to = from + CHILD_PAGE_SIZE - 1;
        const { data, error, count } = await supabase
          .from("scientific_names")
          .select(BASE_SELECT, { count: "exact" })
          .eq("current_parent", parentId)
          .order("name_spell_valid", { ascending: true })
          .range(from, to);
        if (error) throw error;
        mergeNodes(data || []);
        mergeAuthorsFromRows(data || []);
        await ensureDescendantCounts([
          parentId,
          ...(data || []).map((d) => d.id).filter(Boolean),
        ]);
        setFetchedChildren((prev) => {
          const next = new Map(prev);
          const prevPage = next.get(parentId) ?? -1;
          if (page > prevPage) next.set(parentId, page);
          return next;
        });
        setChildrenTotals((prev) => ({
          ...prev,
          [parentId]: typeof count === "number" ? count : Math.max(prev[parentId] || 0, (data || []).length),
        }));
      } catch (err) {
        console.error("[Taxonomy] fetchChildren error:", err);
      } finally {
        setLoadingChildren((prev) => {
          const next = new Set(prev);
          next.delete(parentId);
          return next;
        });
      }
    },
    [fetchedChildren, mergeNodes, ensureDescendantCounts]
  );

  const loadNodeWithAncestors = useCallback(
    async (id) => {
      if (!id) return null;
      let node = allScientificNames.find((n) => n.id === id);
      if (!node) {
        node = await fetchNodeById(id);
      }
      if (!node) return null;

      const ancestorIds = [];
      let cur = node;
      const visited = new Set([cur.id]);
      while (cur?.current_parent && !visited.has(cur.current_parent)) {
        visited.add(cur.current_parent);
        let parent = allScientificNames.find((n) => n.id === cur.current_parent);
        if (!parent) {
          parent = await fetchNodeById(cur.current_parent);
        }
        if (!parent) break;
        ancestorIds.unshift(parent.id);
        cur = parent;
      }

      // 選択されたノードまでのパスを展開する
      // Expand the path to the selected node
      const allIdsToExpand = [...ancestorIds, node.id];
      setExpandedNodes(prev => {
        const next = { ...prev };
        allIdsToExpand.forEach(expId => { next[expId] = true; });
        return next;
      });
      setPersistExpanded(prev => {
        const next = new Set(prev);
        allIdsToExpand.forEach(expId => { next.add(expId); });
        return next;
      });

      await fetchAuthorsForIds([node.id, ...ancestorIds]);
      await ensureDescendantCounts([node.id, ...ancestorIds]);
      // 祖先と対象ノードの子を事前ロード（最小限）
      for (const pid of [node.id, ...ancestorIds]) {
        await fetchChildren(pid);
      }

      const latest = allScientificNames.find((n) => n.id === id) || node;
      setSelectedScientificName(latest);
      setIsInitialLoad(false);
      return latest;
    },
    [allScientificNames, fetchChildren, fetchAuthorsForIds, fetchNodeById, ensureDescendantCounts]
  );

  const getParentLabel = useCallback(
    (opt) => {
      if (!opt) return "";
      const parent = sciMap.get(opt.current_parent);
      if (parent) {
        const parentRank = (parent.current_rank || "").toLowerCase();
        if (parentRank === "subgenus") {
          const genus = sciMap.get(parent.current_parent);
          const genusLabel = genus?.name_spell_valid || genus?.id || "";
          const subLabel = parent.name_spell_valid || parent.id || "";
          return genusLabel ?
            `${t("taxonomy:labels.genus", { name: genusLabel })} / ${t("taxonomy:labels.subgenus", { name: subLabel })}` :
            t("taxonomy:labels.subgenus", { name: subLabel });
        }
        return `${parent.current_rank || t("taxonomy:labels.parentRankFallback")}: ${parent.name_spell_valid || parent.id}`;
      }
      if (opt._parent_label) return t("taxonomy:labels.parent", { name: opt._parent_label });
      return "";
    },
    [sciMap, t]
  );

  const performSearch = useCallback(async () => {
    const normalized = normalizeSearchQuery(searchInput);
    if (!normalized) {
      setDialogSearchResults([]);
      return;
    }
    setDialogSearchLoading(true);
    setDialogSearchResults([]);
    try {
      const { results } = await searchScientificNames({
        query: normalized,
        combineMode,
        matchMode,
        allowFuzzy: fuzzyEnabled,
        limit: 50,
      });
      mergeNodes(results);
      mergeAuthorsFromRows(results);
      setDialogSearchResults(results);
    } catch (err) {
      console.error("[Taxonomy] Search error:", err);
      setDialogSearchResults([]);
    } finally {
      setDialogSearchLoading(false);
    }
  }, [searchInput, combineMode, matchMode, fuzzyEnabled, mergeNodes, mergeAuthorsFromRows]);

  // 初期データ: superfamily を1件取得して初期表示
  useEffect(() => {
    let mounted = true;
    if (!mounted) return;
    (async () => {
      try {
        setInitializing(true);
        setInitialLoadError(null);
        const { data, error } = await supabase
          .from("scientific_names")
          .select(BASE_SELECT)
          .eq("current_rank", "superfamily")
          .ilike("name_spell_valid", "%Ichneumonoidea%")
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (data) {
          mergeNodes([data]);
          const loaded = await loadNodeWithAncestors(data.id);
          if (loaded) {
            // ルートは初期から展開状態にしておく
            setExpandedNodes((prev) => ({ ...prev, [loaded.id]: true }));
            setPersistExpanded(new Set([loaded.id]));
          }
        }
      } catch (err) {
        console.error("[Taxonomy] initial load error", err);
        setInitialLoadError(err.message || t("taxonomy:error.message"));
      } finally {
        setInitializing(false);
      }
    })();
    return () => {
      mounted = false;
    };
  // run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // --- タブ管理 ---
  const handleTabChange = useCallback((_, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleOpenInTab = useCallback(async (node) => {
    if (!node?.id) return;

    if (tabs.some(tab => tab.id === node.id)) {
      setActiveTab(node.id);
      return;
    }

    const newTab = {
      id: node.id,
      label: node.name_spell_valid || t('taxonomy:tab.details'),
    };
    setTabs(prev => [...prev, newTab]);
    setTabData(prev => ({ ...prev, [node.id]: { node } }));
    setActiveTab(node.id);

    // 監査ログを非同期で取得してタブデータにセット
    const { data, error } = await supabase
      .from("audit_log")
      .select("row_id, actor_id, created_at")
      .eq("table_name", "scientific_names")
      .eq("row_id", node.id)
      .order("created_at", { ascending: false })
      .limit(1);
    if (!error && data?.[0]) {
      setTabData(prev => ({ ...prev, [node.id]: { ...prev[node.id], latestAudit: data[0] } }));
      if (data[0].actor_id) {
        try {
          const members = await fetchMembersWithFallback([data[0].actor_id]);
          mergeMemberNames(members);
        } catch (err) {
          console.error("failed to fetch actor name", err);
        }
      }
    }
  }, [tabs, t, fetchMembersWithFallback, mergeMemberNames]);

  const handleCloseTab = useCallback((e, tabId) => {
    e.stopPropagation(); // 親のタブクリックイベントを発火させない
    if (tabId === 'tree') return;

    setTabs(prev => prev.filter(tab => tab.id !== tabId));
    setTabData(prev => {
      const next = { ...prev };
      delete next[tabId];
      return next;
    });

    if (activeTab === tabId) {
      setActiveTab('tree');
    }
  }, [activeTab]);

  // --- URLパラメータを処理してタブを開く ---
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focusId = params.get('focus');
    // 初期化中でなく、focusIdがある場合
    if (focusId && !initializing) {
      // URLからクエリを削除して再読み込み時の再実行を防ぐ
      navigate('/taxonomy/taxon-explore', { replace: true });

      // 既存のタブがあればアクティブにし、なければ新しいタブを開く
      if (tabs.some(tab => tab.id === focusId)) {
        setActiveTab(focusId);
      } else {
        (async () => {
          setOverlayLoading(true);
          const node = await loadNodeWithAncestors(focusId);
          if (node) handleOpenInTab(node);
          setOverlayLoading(false);
        })();
      }
    }
  }, [location.search, initializing, tabs, loadNodeWithAncestors, handleOpenInTab, navigate]);

  // サーチ選択
  const handleSearchChange = useCallback(async (_, newValue) => {
    if (!newValue) return;
    const node = await loadNodeWithAncestors(newValue.id || newValue.value || newValue);
    if (node) {
      handleOpenInTab(node);
    }
    setSearchDialogOpen(false);
  }, [loadNodeWithAncestors, handleOpenInTab]);

  const handleOpenSearchDialog = () => {
    setSearchDialogOpen(true);
  };

  const handleCloseSearchDialog = () => {
    setSearchDialogOpen(false);
  };

  // 自動スクロール
  useEffect(() => {
    if (
      !initializing &&
      !isInitialLoad && // 最初のロード時（ページを開いた直後）はスクロールしない
      selectedScientificName?.id
    ) {
      // ツリーの展開アニメーションとレンダリングには時間がかかるため、少し遅延させてからスクロールを実行します。
      // これにより、展開が完了した後に目的のノードがDOMに描画され、スクロールが正しく動作します。
      const scrollTimer = setTimeout(() => {
        // TaxonomicTree内でハイライトされているノード（.Mui-selectedクラスを持つTreeItem）を探します。
        // This assumes the highlighted node in TaxonomicTree has the '.Mui-selected' class.
        const targetNodeElement = document.querySelector('.Mui-selected');

        if (targetNodeElement) {
          // 目的のノードを画面の上端にスムーズにスクロールします。
          targetNodeElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        } else if (resultsContainerRef.current) {
          // フォールバックとして、ツリー全体のコンテナにスクロールします。
          resultsContainerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 500); // 500msの遅延（展開アニメーションを考慮）

      return () => clearTimeout(scrollTimer);
    }
  }, [selectedScientificName, initializing, isInitialLoad]);

  // ローディング or エラー
  if (initializing) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
          }}
        >
          <CircularProgress color="primary" size={60} />
          <Typography sx={{ mt: 2, color: "text.secondary" }}>
            {t('taxonomy:loading.initial')}
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }
  if (initialLoadError) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Container maxWidth="sm" sx={{ py: 5, textAlign: "center" }}>
          <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit" />}>
            <AlertTitle>{t('taxonomy:error.title')}</AlertTitle>
            {initialLoadError}
          </Alert>
        </Container>
      </ThemeProvider>
    );
  }

  // 本体レンダリング
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 2 }}
        open={overlayLoading}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2,
            borderRadius: 3,
            background: "linear-gradient(135deg, rgba(0,0,0,0.65), rgba(30,30,30,0.9))",
            boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
          }}
        >
          <CircularProgress color="inherit" />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 700, color: "common.white" }}>
              {t('taxonomy:loading.exploring')}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
              {t('taxonomy:loading.wait')}
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      {/* pt: 0 にしてコンテナ上部のパディングをなくし、タブがヘッダー直下に配置されるようにする */}
      {/* Set pt: 0 to remove top padding, allowing tabs to be placed directly under the header */}
      <Container maxWidth="lg" sx={{ pt: 0, pb: 4 }}>
        {/* Sticky Header with Search and Tabs */}
        <Box sx={{
          position: 'sticky',
          // サイトのグローバルヘッダーの高さに合わせてtopの値を調整してください (例: 64px)
          // Adjust to match the height of the site's global header (e.g., 64px)
          top: 64,
          zIndex: 10,
          bgcolor: 'background.default',
        }}>
          <Paper elevation={2}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1, pl: 2 }}>
              <Tooltip title={t('taxonomy:searchDialog.tooltip')}>
                <IconButton onClick={handleOpenSearchDialog} color="primary">
                  <SearchIcon />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{ flexGrow: 1 }}>
                {tabs.map(tab => (
                  <Tab
                    key={tab.id}
                    value={tab.id}
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" sx={{ textTransform: 'none' }}>{tab.label}</Typography>
                        {tab.id !== 'tree' && (
                          <Box
                            role="button"
                            aria-label={t('common:close')}
                            onClick={(e) => handleCloseTab(e, tab.id)}
                            sx={{
                              ml: 1,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" }
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </Box>
                        )}
                      </Stack>
                    }
                  />
                ))}
              </Tabs>
            </Stack>
          </Paper>
        </Box>

        {/* Content (mt: 2でタブとの間にスペースを確保) */}
        <Paper elevation={2} sx={{ mt: 2 }}>
          <Box sx={{ p: { xs: 1, sm: 2 }, overflow: "auto", whiteSpace: "normal", wordBreak: "break-word" }}>
            {tabs.map(tab => (
              <Box key={tab.id} role="tabpanel" hidden={activeTab !== tab.id}>
                {activeTab === tab.id && (
                  <>
                    {tab.id === 'tree' ? (
                      <Box>
                        <Box ref={resultsContainerRef} sx={{ mt: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <AccountTreeIcon sx={{ color: "primary.main" }} />
                            <Typography variant="h6">{t('taxonomy:tree.title')}</Typography>
                          </Stack>
                        {selectedScientificName ? (
                          <TaxonomicTree
                            rootScientificName={selectedScientificName}
                            allScientificNames={allScientificNames}
                            scientificNameAuthors={scientificNameAuthors}
                            descendantCountMap={descendantCountMap}
                            highlightId={selectedScientificName.id}
                            onSelectScientificName={handleOpenInTab}
                            onFetchChildren={fetchChildren}
                            fetchedChildren={fetchedChildren}
                            loadingChildren={loadingChildren}
                            expandedNodes={expandedNodes}
                            setExpandedNodes={setExpandedNodes}
                            persistExpanded={persistExpanded}
                            setPersistExpanded={setPersistExpanded}
                            childrenTotals={childrenTotals}
                            childPageSize={CHILD_PAGE_SIZE}
                          />
                        ) : (
                          <Typography color="text.secondary" textAlign="center" sx={{ my: 5 }}>
                            {t('taxonomy:tree.noTaxonSelected')}
                          </Typography>
                        )}
                        </Box>
                      </Box>
                    ) : (
                      tabData[tab.id]?.node && (
                        <Box>
                          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                            {(() => {
                                const sn = tabData[tab.id]?.node;
                                if (!sn) return null;

                                const isInvalid = isInvalidRankName(sn.current_rank) || (sn.current_invalid_type && sn.current_invalid_type !== 'valid');
                                const validNameNode = isInvalid && sn.current_parent ? sciMap.get(sn.current_parent) : null;
                                
                                let html = formatSingleNodeName(sn);
                                if (isInvalid && validNameNode) {
                                    const invalidTypeLabel = sn.current_invalid_type || sn.current_rank;
                                    html += ` ${invalidTypeLabel} under ${formatSingleNodeName(validNameNode)}`;
                                }

                                return (
                                    <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ width: "100%" }}>
                                        <Box sx={{ pt: 0.5 }}>
                                            {isInvalid ? (
                                                <Tooltip title={`Invalid: ${sn.current_invalid_type || sn.current_rank}`}>
                                                    <HighlightOffIcon color="error" />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Valid Name">
                                                    <CheckCircleOutlineIcon color="success" />
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Typography
                                          variant="h5"
                                          component="div"
                                          dangerouslySetInnerHTML={{ __html: html }}
                                          sx={{ lineHeight: 1.3, fontWeight: 700, wordBreak: "break-word", flex: 1 }}
                                        />
                                    </Stack>
                                );
                            })()}
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
                              {tabData[tab.id].node?.current_rank && (
                                <Chip
                                  size="medium"
                                  icon={<CategoryIcon fontSize="small" />}
                                  label={t("taxonomy:details.rank", { rank: tabData[tab.id].node.current_rank })}
                                  color="default"
                                  sx={{
                                    height: 26,
                                    fontWeight: 700,
                                    "& .MuiChip-label": { px: 1, fontSize: "0.8rem", fontWeight: 700, letterSpacing: 0.3 },
                                    "& .MuiChip-icon": { fontSize: "1.1rem" },
                                    bgcolor: "rgba(127,255,212,0.08)",
                                    border: "1px solid rgba(127,255,212,0.4)",
                                    color: "text.secondary",
                                  }}
                                />
                              )}
                              {latestAuditText && (
                                <Chip
                                  size="medium"
                                  label={latestAuditText}
                                  color="default"
                                  sx={{
                                    height: 26,
                                    fontWeight: 700,
                                    "& .MuiChip-label": { px: 0.9, fontSize: "0.78rem", fontWeight: 700, letterSpacing: 0.2 },
                                    bgcolor: "rgba(127,255,212,0.08)",
                                    border: "1px solid rgba(127,255,212,0.3)",
                                    color: "text.secondary",
                                  }}
                                />
                              )}
                              {tabData[tab.id].node?.extant_fossil === 'fossil' && (
                                <Chip
                                  icon={<FilterHdrIcon />}
                                  label={t('taxonomy:fossil', 'Fossil')}
                                  size="medium"
                                  sx={{
                                    height: 26,
                                    backgroundColor: '#8B4513', // SaddleBrown
                                    color: 'white',
                                    fontWeight: 700,
                                    "& .MuiChip-label": { px: 1, fontSize: "0.8rem", letterSpacing: 0.3 },
                                    "& .MuiChip-icon": { color: 'white', ml: '6px', fontSize: '1.1rem' },
                                  }}
                                />
                              )}
                            </Stack>
                          </Box>
                          <DialogDisplayScientificNameDetails
                            key={tab.id} // タブIDをキーとして使用し、タブ切り替え時にコンポーネントを完全に再マウントさせる
                            initialScientificName={tabData[tab.id].node}
                            allScientificNames={allScientificNames}
                            onNodeClick={handleOpenInTab}
                            scientificNameAuthors={scientificNameAuthors}
                            allTaxonomicActs={[]}
                            allReferences={{ data: [] }} // 文献は別途取得が必要なら実装
                            descendantCountMap={descendantCountMap}
                            onFetchChildren={fetchChildren}
                            fetchedChildren={fetchedChildren}
                            isPage={true} // isPageはスタイリング用なので流用
                          />
                        </Box>
                      )
                    )}
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Paper>

        <Dialog open={searchDialogOpen} onClose={handleCloseSearchDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{t('taxonomy:searchDialog.title')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction="row" spacing={1}>
                <TextField
                  label={t('taxonomy:searchDialog.placeholder')}
                  size="small"
                  autoFocus
                  fullWidth
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      performSearch();
                    }
                  }}
                />
                <Button onClick={performSearch} disabled={dialogSearchLoading} variant="contained" sx={{ whiteSpace: 'nowrap' }}>
                  {t('taxonomy:searchDialog.button')}
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                <ToggleButtonGroup size="small" exclusive value={combineMode} onChange={(_, v) => v && setCombineMode(v)}>
                  <ToggleButton value="AND">{t('taxonomy:searchDialog.and')}</ToggleButton>
                  <ToggleButton value="OR">{t('taxonomy:searchDialog.or')}</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup size="small" exclusive value={matchMode} onChange={(_, v) => v && setMatchMode(v)}>
                  <ToggleButton value="partial">{t('taxonomy:searchDialog.partialMatch')}</ToggleButton>
                  <ToggleButton value="exact">{t('taxonomy:searchDialog.exactMatch')}</ToggleButton>
                </ToggleButtonGroup>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={fuzzyEnabled}
                      onChange={(e) => setFuzzyEnabled(e.target.checked)}
                    />
                  }
                  label={t('taxonomy:searchDialog.fuzzy')}
                />
              </Stack>
              <Box sx={{ mt: 2, minHeight: 240, maxHeight: 400, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {dialogSearchLoading ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: 240 }}>
                    <CircularProgress />
                  </Stack>
                ) : dialogSearchResults.length > 0 ? (
                  <List>
                    {dialogSearchResults.map((opt) => (
                      <ListItemButton key={opt.id} onClick={() => handleSearchChange(null, opt)}>
                        <ListItemText
                          primary={formatScientificNameLabel(opt)}
                          secondary={
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {opt.current_rank} {getParentLabel(opt) ? ` | ${getParentLabel(opt)}` : ""}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                ) : (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 2 }}>
                    <Typography color="text.secondary" textAlign="center">
                      {t('taxonomy:searchDialog.prompt')}
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Stack>
          </DialogContent>
        </Dialog>

      </Container>
    </ThemeProvider>
  );
}
